/**
 * Document canvas — Gemini-style side-by-side pane for `kind: "document"`
 * artifacts. Always-editable: drops the explicit edit/preview toggle in
 * favor of a TipTap editor that handles both reading and writing in one
 * surface, with debounced auto-save back to /api/artifacts/:id.
 *
 * Routed to from `artifact.tsx`, the kind-aware dispatcher.
 *
 * @module components/artifacts/document-canvas
 */

import { type Editor } from "@tiptap/react";
import { motion } from "framer-motion";
import { Code2, Download, FileText, FileType2, Loader2, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  downloadArtifactDocx,
  downloadArtifactPdf,
} from "@/lib/artifacts/download";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useArtifactStore } from "@/store/artifact-store";
import { api } from "@/utils/api-client";
import { ArtifactErrorBanner } from "./artifact-error-banner";
import { ArtifactUpdateOverlay } from "./artifact-update-overlay";
import { DocumentArtifact } from "./document-artifact";
import { FocusModeToggle } from "./focus-mode-toggle";
import { EditorToolbar } from "./editor-toolbar";

/**
 * Shape of the response from `GET /api/artifacts/:id`. Loose typing keeps the
 * canvas decoupled from the server schema; only fields actually used here are
 * declared.
 */
interface FetchedArtifact {
  id: string;
  title: string;
  content: string;
  updatedAt?: string;
}

/**
 * How long the editor stays quiet after a keystroke before auto-saving.
 *
 * Long enough that mid-paragraph thinking pauses don't trigger a round-trip
 * (the previous 1.5s window felt twitchy — the editor would "Saving…" with
 * every comma). Pending edits still flush on canvas close so nothing is
 * lost when the user navigates away inside this window.
 */
const AUTO_SAVE_DEBOUNCE_MS = 5000;

type SaveStatus = "idle" | "saving" | "saved" | "error";

/**
 * Document-kind canvas. Wraps the shared {@link DocumentArtifact} editor with
 * the canvas chrome — header, formatting toolbar, source-view toggle, PDF/DOCX
 * download, and debounced auto-save. The editor itself (extensions, content
 * sync, diff-highlight) lives in `DocumentArtifact` so the Library renders
 * documents through the exact same component, read-only. The kind dispatcher
 * in `artifact.tsx` decides when to mount this vs the html or presentation
 * canvas.
 */
export function DocumentCanvas() {
  const artifact = useArtifactStore((s) => s.artifact);
  const setArtifact = useArtifactStore((s) => s.setArtifact);
  const hideArtifact = useArtifactStore((s) => s.hideArtifact);

  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
  const [fetchedAt, setFetchedAt] = useState<string | undefined>(undefined);
  /**
   * Tracks the in-flight PDF/DOCX render so the toolbar trigger can swap to
   * a spinner. Server-side render takes several seconds; without feedback
   * users click again thinking nothing happened.
   */
  const [isDownloading, setIsDownloading] = useState(false);

  /**
   * Toggles between the rich-text editor and a raw-markdown text view so
   * power users can inspect or edit the underlying syntax. Both views share
   * the same `handleEditorChange` flow; switching modes preserves edits
   * because the source view writes back into the artifact store the same
   * way the editor does.
   */
  const [viewMode, setViewMode] = useState<"rich" | "source">("rich");

  /**
   * Holds the markdown the user has typed since the last successful save.
   * Distinct from `artifact.content` because that mirrors what's in the
   * store (latest streamed body or last known canonical), while this is
   * the in-flight edit waiting for the debounce window to close.
   */
  const pendingSaveRef = useRef<string | null>(null);
  /**
   * The artifact id that owns the pending edit. Captured when the edit is
   * stashed so a flush that fires after the user switches artifacts (e.g. on
   * unmount) saves to — and repaints — the correct artifact, never clobbering
   * the newly-opened one (a document's markdown landing in a spreadsheet).
   */
  const pendingSaveIdRef = useRef<string | null>(null);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * The shared editor instance, handed up by {@link DocumentArtifact} via
   * `onEditorReady`. Lives here so the header toolbar can drive it; null
   * until the editor mounts (or while the source view is showing).
   */
  const [editor, setEditor] = useState<Editor | null>(null);

  /**
   * Auto-scroll plumbing for streaming. `scrollRef` points at the canvas
   * scroll container; `stickyBottomRef` tracks whether the user is parked
   * near the bottom (so the page follows the stream) or has scrolled up
   * to read earlier content (in which case we leave their position alone).
   * The threshold is generous because token streaming is choppy and
   * exact-bottom rarely lands cleanly during a write.
   */
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const stickyBottomRef = useRef(true);

  function handleCanvasScroll() {
    const el = scrollRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    stickyBottomRef.current = distanceFromBottom < 120;
  }

  const isStreaming = artifact.status === "streaming";
  const isUpdateInFlight = artifact.pendingContent !== null;

  /**
   * Debounced edit handler passed to {@link DocumentArtifact} as `onChange`.
   * Stashes the latest markdown and (re)starts the timer; when it fires we
   * commit through `flushSave`. Skip during streaming so the model's deltas
   * don't loop back through PUT, and skip no-op echoes of the canonical body.
   */
  const handleEditorChange = useCallback(
    (markdown: string) => {
      const status = useArtifactStore.getState().artifact.status;
      if (status === "streaming") return;
      if (markdown === useArtifactStore.getState().artifact.content) return;
      pendingSaveRef.current = markdown;
      pendingSaveIdRef.current = useArtifactStore.getState().artifact.id;
      setSaveStatus("saving");
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => {
        void flushSave();
      }, AUTO_SAVE_DEBOUNCE_MS);
    },
    // flushSave is defined below; React lint warning is fine — both are
    // stable across renders thanks to useCallback.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  /** Clears the store baseline after DocumentArtifact fades the diff bands. */
  const handlePreviousContentApplied = useCallback(() => {
    useArtifactStore.getState().clearPreviousContent();
  }, []);

  /**
   * Auto-scroll the canvas to the bottom as create-stream content lands.
   * Honors `stickyBottomRef` so users who scrolled up to read keep their
   * position. Only runs on `content` because update streams buffer into
   * `pendingContent` invisibly — the canvas doesn't display them, so
   * there's nothing to scroll to until the finish swap.
   *
   * `requestAnimationFrame` defers the scroll until after TipTap has laid
   * out the new content — reading scrollHeight synchronously would catch
   * the pre-update height and snap short.
   *
   * No cleanup that cancels the rAF: during fast streams, deltas arrive
   * faster than 16ms, so an aggressive cleanup would cancel each pending
   * scroll before the frame fires and the canvas would never follow. Each
   * rAF is a single property write — cheap to let them all run; only the
   * final one's scrollTop value lands.
   */
  useEffect(() => {
    if (!stickyBottomRef.current) return;
    const el = scrollRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight;
    });
  }, [artifact.content]);

  /**
   * Reset the sticky flag whenever a new artifact opens so we always start
   * by following the stream — even if the previous canvas was scrolled up.
   */
  useEffect(() => {
    stickyBottomRef.current = true;
  }, [artifact.id]);

  /**
   * Fetch the canonical artifact body when the canvas opens or the id changes.
   * Streamed deltas reach the store first, but a fresh open after a page
   * reload needs the persisted body since the data stream is gone.
   */
  useEffect(() => {
    if (!artifact.isVisible || !artifact.id || artifact.status === "streaming") return;
    let cancelled = false;
    const fetchId = artifact.id;
    api
      .get(`/artifacts/${fetchId}`)
      .then((response) => {
        if (cancelled) return;
        // Guard against a late response for an artifact the user has since
        // switched away from: applying it would clobber the now-open
        // artifact's content with the previous one's body (e.g. a sheet
        // rendering the prior document's text). Only paint if still current.
        if (useArtifactStore.getState().artifact.id !== fetchId) return;
        const fetched = (response as { data?: FetchedArtifact })?.data;
        if (!fetched) return;
        setFetchedAt(fetched.updatedAt);
        if (fetched.content && fetched.content !== artifact.content) {
          setArtifact({ content: fetched.content, title: fetched.title });
        }
      })
      .catch(() => {
        // Silent — the store may already have streamed content; if not,
        // the canvas just shows what's there.
      });
    return () => {
      cancelled = true;
    };
  }, [artifact.isVisible, artifact.id, artifact.status, setArtifact]);

  /**
   * Persists the latest pending edit. Used by the debounced auto-save and
   * by the unmount cleanup below so an in-flight edit isn't lost when the
   * user closes the canvas before the debounce window elapses.
   */
  const flushSave = useCallback(async () => {
    // Save to the artifact that OWNS this edit, not whatever is open now — a
    // flush firing on unmount after a switch must not write the document's
    // markdown into the newly-opened artifact.
    const id = pendingSaveIdRef.current ?? useArtifactStore.getState().artifact.id;
    const title = useArtifactStore.getState().artifact.title;
    const next = pendingSaveRef.current;
    if (!id || next === null) return;
    pendingSaveRef.current = null;
    pendingSaveIdRef.current = null;
    setSaveStatus("saving");
    try {
      const response = await api.put(`/artifacts/${id}`, { title, content: next });
      const updated = (response as { data?: FetchedArtifact })?.data;
      // Only repaint the store if the saved artifact is still the open one;
      // otherwise this would clobber the artifact the user switched to.
      if (updated && useArtifactStore.getState().artifact.id === id) {
        setArtifact({ content: updated.content, title: updated.title });
        setFetchedAt(updated.updatedAt);
      }
      setSaveStatus("saved");
    } catch (err) {
      setSaveStatus("error");
      toast.error(err instanceof Error ? err.message : "Failed to save artifact");
    }
  }, [setArtifact]);

  /**
   * Flush any pending edit and clear the timer when the canvas closes or
   * unmounts so users never lose work to a half-elapsed debounce window.
   */
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      if (pendingSaveRef.current !== null) {
        // Fire-and-forget; the promise outlives the React lifecycle.
        void flushSave();
      }
    };
  }, [flushSave]);

  /**
   * Downloads the current artifact body as a server-rendered PDF via the
   * shared download helper, which handles auth, blob streaming, and
   * filename safety identically across every per-kind canvas.
   */
  async function handleDownloadPdf() {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      await downloadArtifactPdf({ id: artifact.id, title: artifact.title });
    } finally {
      setIsDownloading(false);
    }
  }

  /**
   * Downloads the current artifact body as a server-rendered DOCX. Only
   * exposed on the document canvas because `html` / `presentation` kinds
   * don't have a faithful Word mapping — those canvases keep PDF-only.
   */
  async function handleDownloadDocx() {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      await downloadArtifactDocx({ id: artifact.id, title: artifact.title });
    } finally {
      setIsDownloading(false);
    }
  }


  return (
    <motion.div
      // Gemini-canvas-style entrance: slide in from the right while fading
      // and scaling up slightly. Exit reverses the path so closing feels
      // symmetric. AnimatePresence in chat.tsx drives mount/unmount.
      initial={{ opacity: 0, x: 80, scale: 0.96 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 80, scale: 0.96 }}
      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      // `flex-[3]` deliberately outweighs the chat's `flex-1` so the chat
      // column shrinks to roughly a quarter of the available row width —
      // narrow enough that its self-measured width hook flips to compact
      // mode without the chat knowing this canvas exists.
      className="relative flex h-full min-w-0 flex-[3] flex-col border-l bg-background shadow-2xl max-lg:fixed max-lg:inset-0 max-lg:z-40 max-lg:flex-none max-lg:border-l-0"
    >
      <header className="flex shrink-0 items-center gap-2 border-b bg-background px-3 py-2 lg:gap-3 lg:px-4 lg:py-2.5">
        {/**
         * Title + status, far left. `min-w-0 flex-1` lets the title truncate
         * gracefully so the toolbar to its right never wraps onto a new line.
         */}
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-base font-semibold leading-tight">
            {artifact.title || "Untitled"}
          </h2>
          <SubtitleRow
            fetchedAt={fetchedAt}
            isStreaming={isStreaming}
            isUpdateInFlight={isUpdateInFlight}
            saveStatus={saveStatus}
          />
        </div>

        {/**
         * Formatting toolbar — operates on the editor instance above.
         * Disabled in source view (toolbar commands target rich nodes that
         * don't exist there) and during streaming.
         *
         * Hidden on phones (it overflows the header and would push Close
         * off-screen); horizontally scrollable on tablet; natural width on
         * desktop. Phone users can still format via the markdown source view.
         */}
        <div className="hidden min-w-0 shrink overflow-x-auto md:block lg:shrink-0">
          <EditorToolbar editor={editor} disabled={isStreaming || viewMode === "source"} />
        </div>

        <div className="hidden h-6 w-px bg-border lg:block" />

        {/* Action cluster — always pinned and visible so Close stays reachable
            on every screen size, regardless of toolbar width. */}
        <div className="flex shrink-0 items-center gap-0.5">
        {/* Source/rich toggle — power users inspect or hand-edit markdown. */}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => setViewMode((m) => (m === "rich" ? "source" : "rich"))}
          title={viewMode === "rich" ? "View markdown source" : "View rendered"}
          aria-pressed={viewMode === "source"}
        >
          {viewMode === "rich" ? <Code2 className="size-5" /> : <FileText className="size-5" />}
        </Button>

        {/* Right-side actions — Download / Close. Share intentionally
            omitted: artifact sharing is not yet supported. Re-add when
            we have an actual share endpoint to call. */}
        {/**
         * Download menu — document artifacts can render as either PDF (print
         * fidelity, link-friendly) or DOCX (editable in Word, easier to hand
         * off to non-technical reviewers). Both run through the same server
         * endpoints and share filename/auth handling in `download.ts`.
         */}
        <FocusModeToggle />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              disabled={isDownloading}
              title={isDownloading ? "Preparing download…" : "Download"}
            >
              {isDownloading ? (
                <Loader2 className="size-5 animate-spin" />
              ) : (
                <Download className="size-5" />
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={handleDownloadPdf}>
              <FileText className="mr-2 size-4" />
              Download as PDF
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={handleDownloadDocx}>
              <FileType2 className="mr-2 size-4" />
              Download as DOCX
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <Button type="button" variant="ghost" size="icon" onClick={hideArtifact} title="Close">
          <X className="size-5" />
        </Button>
        </div>
      </header>

      <div
        ref={scrollRef}
        onScroll={handleCanvasScroll}
        className="flex-1 overflow-auto"
      >
        {viewMode === "source" ? (
          /**
           * Raw markdown source view. Edits flow through the same auto-save
           * path as the rich editor; switching back to rich view triggers
           * the value-sync effect, which re-parses the markdown into the
           * TipTap document so changes are preserved across modes.
           */
          <div className="mx-auto w-full max-w-4xl px-4 py-6 lg:px-10 lg:py-10">
            <textarea
              value={artifact.content}
              onChange={(e) => handleEditorChange(e.target.value)}
              readOnly={isStreaming}
              spellCheck={false}
              className="min-h-[60vh] w-full resize-none border-0 bg-transparent font-mono text-sm leading-relaxed outline-none"
              placeholder="Markdown source…"
            />
          </div>
        ) : (
          <DocumentArtifact
            content={artifact.content}
            previousContent={artifact.previousContent}
            editable={!isStreaming}
            onChange={handleEditorChange}
            onEditorReady={setEditor}
            onPreviousContentApplied={handlePreviousContentApplied}
          />
        )}
      </div>

      {/**
       * Rich in-flight update surface (replaces the old tiny "Updating…"
       * pill — issue #961). Streams the new body in live with a progress
       * bar, live metric, and elapsed timer. The editor above stays mounted
       * with the previous body, so the finish→diff-highlight handoff is
       * unaffected. Self-gates on the store's `pendingContent`.
       */}
      <ArtifactUpdateOverlay />

      {/* Error + retry banner — shown only when an update stream fails. */}
      <ArtifactErrorBanner />
    </motion.div>
  );
}

/**
 * Renders the subtitle row beneath the canvas title.
 *
 * Format: `[time ago] | [active status]` — both pieces optional. Time is
 * always shown when known so the user has a stable anchor of "when did
 * this last persist"; the status side only appears when something is in
 * flight (streaming, updating, saving) or a transient confirmation is
 * worth surfacing (saved, save failed).
 */
function SubtitleRow({
  fetchedAt,
  isStreaming,
  isUpdateInFlight,
  saveStatus,
}: {
  fetchedAt: string | undefined;
  isStreaming: boolean;
  isUpdateInFlight: boolean;
  saveStatus: SaveStatus;
}) {
  const timeLabel = fetchedAt ? updatedLabel(fetchedAt) : null;
  const statusLabel = activeStatusLabel({ isStreaming, isUpdateInFlight, saveStatus });

  // Fresh streams have no persisted timestamp yet — show only the active
  // status so the user isn't told "Recently updated" before the first save.
  if (!timeLabel && !statusLabel) return <span className="text-xs text-muted-foreground">&nbsp;</span>;

  return (
    <span className="text-xs text-muted-foreground">
      {timeLabel}
      {timeLabel && statusLabel ? <span className="mx-1.5 opacity-50">|</span> : null}
      {statusLabel}
    </span>
  );
}

/**
 * Picks the active status string for the subtitle, or null if the editor
 * is idle with nothing transient to report.
 */
function activeStatusLabel({
  isStreaming,
  isUpdateInFlight,
  saveStatus,
}: {
  isStreaming: boolean;
  isUpdateInFlight: boolean;
  saveStatus: SaveStatus;
}): string | null {
  if (isUpdateInFlight) return "Updating…";
  if (isStreaming) return "Generating…";
  if (saveStatus === "saving") return "Saving…";
  if (saveStatus === "saved") return "Saved";
  if (saveStatus === "error") return "Save failed";
  return null;
}

/**
 * Renders a human-readable "last updated" label, falling back to the generic
 * label when no timestamp is available (e.g. the canvas opened from a fresh
 * stream that hasn't been fetched yet).
 */
function updatedLabel(timestamp?: string): string {
  if (!timestamp) return "Recently updated";
  const updated = new Date(timestamp);
  if (Number.isNaN(updated.getTime())) return "Recently updated";
  const diffMs = Date.now() - updated.getTime();
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} day${days === 1 ? "" : "s"} ago`;
  return updated.toLocaleDateString();
}
